import React, { useEffect, useState } from "react";
import {
  LeftOutlined,
  RightOutlined,
  UserOutlined,
  GlobalOutlined,
  SettingOutlined,
  BarChartOutlined,
  PieChartOutlined,
  SnippetsOutlined,
  PlusOutlined,
  FileProtectOutlined,
  ScheduleOutlined,
  PartitionOutlined,
} from "@ant-design/icons";
import { Avatar, Badge, Popover, Image, ConfigProvider } from "antd";
import { Layout, Menu } from "antd";
import { useNavigate } from "react-router-dom";
import TabbedWorkspace from "../tabs/TabbedWorkspace";
import accountsApi from "../api/Accounts/AccountsApi";
import { resetAppSettings } from "../api/appSettings";
import MenuItem from "antd/es/menu/MenuItem";
import { ApiService, Template } from "../api";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import type { MenuProps } from "antd";
import { APP_BG } from "../theme/antdTheme";
import { space } from "../theme/space";
import MyProfileModal from "../components/MyProfileModal";
import {
  refreshMyActionsCount,
  resetMyActionsCount,
  useMyActionsStore,
} from "../store/myActionsStore";

const { Header, Content, Footer, Sider } = Layout;

// --- Sider styling (only used in this layout) ---
const SIDER_BG = "#0C2E5C"; // dark navy sidebar
const SIDER_SELECTED = "#1668dc"; // selected menu item highlight
const SIDER_WIDTH = 240; // fits the longest menu label; 280 was rail for
// rail's sake — 40px of every screen back, permanently.

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  onClick?: () => void,
  children?: MenuItem[],
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    onClick,
  } as MenuItem;
}

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // Only the per-template entries are held in state — they come from the
  // server. The fixed entries are rebuilt on every render so things that change
  // (the My Actions count, the collapsed sider) actually reach the menu.
  const [templateItems, setTemplateItems] = useState<MenuItem[]>([]);
  const myActionsCount = useMyActionsStore((s) => s.count);
  // Kept apart from the total because it is the number that changes what
  // somebody does next: red means late, blue means merely waiting.
  const myActionsOverdue = useMyActionsStore((s) => s.overdue);
  const [profileOpen, setProfileOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const userInfo = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo") || "{}")
    : null;

  const systemMenuItems: MenuItem[] = [
    getItem(t("New"), "new", <PlusOutlined />, () => {
      navigate("/");
    }),
    getItem(t("Analytics"), "analytics", <PieChartOutlined />, () => {
      navigate("/analytics");
    }),
    // Saved reports sit beside Analytics on purpose: Analytics is the fixed
    // dashboard, Reports is the question you asked yourself.
    getItem(t("Reports"), "reports", <BarChartOutlined />, () => {
      navigate("/reports");
    }),
    getItem(t("My Requests"), "my_requests", <SnippetsOutlined />, () => {
      navigate("/my-requests");
    }),
    getItem(
      // A flex row, not <Space>: Space wraps each child in a block that
      // inherits the menu item's 40px line-height, and the badge — an
      // inline-block on `vertical-align: middle` — then sits on the strut's
      // own baseline rather than the label's centre, 2.5px low. As direct
      // flex items the two are centred against each other exactly.
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <span>{t("My Actions")}</span>
        {myActionsCount > 0 && (
          <Badge
            count={myActionsCount}
            overflowCount={99}
            // antd's default badge is red, which is the alarm colour -- so red
            // is reserved for work that is actually late and everything else
            // wears the neutral blue. A permanently red badge stops meaning
            // anything the second week somebody has it.
            color={myActionsOverdue > 0 ? undefined : "#2a78d6"}
            title={
              myActionsOverdue > 0
                ? t("{{count}} overdue", { count: myActionsOverdue })
                : undefined
            }
            // The sider is dark navy, so antd's default shadow ring reads as a
            // smudge rather than a lift.
            style={{ boxShadow: "none" }}
          />
        )}
      </span>,
      "my_approvals",
      // Collapsed, the label is gone and the number with it. A dot at least
      // still says "something is waiting"; the count is in the hover tooltip.
      collapsed && myActionsCount > 0 ? (
        <Badge dot offset={[0, 2]}>
          <FileProtectOutlined style={{ color: "inherit" }} />
        </Badge>
      ) : (
        <FileProtectOutlined />
      ),
      () => {
        navigate("/my-approvals");
      },
    ),
  ];

  const isAdmin = Boolean(
    userInfo?.isAdmin ?? userInfo?.is_staff ?? userInfo?.is_superuser,
  );

  const administrationMenuItems: MenuItem[] = [
    getItem(t("Works Plan"), "works_plan", <ScheduleOutlined />, () => {
      navigate("/works-plan");
    }),
    // Designing a template's process is an admin-only area. The server checks
    // again per template; this only keeps the entry out of everyone else's way.
    ...(isAdmin
      ? [
          getItem(t("Process"), "process", <PartitionOutlined />, () => {
            navigate("/process");
          }),
        ]
      : []),
    // Template management is an admin-only area. This was switched off while
    // the page behind it was a stub; it now creates and sets up real templates,
    // so it is the only way into that flow.
    ...(isAdmin
      ? [
          getItem(t("Settings"), "settings", <SettingOutlined />, undefined, [
            getItem(t("Templates"), "templates", null, () => {
              navigate("/templates");
            }),
            // People, groups, the org chart and the directory connection. It
            // lives under Settings rather than beside Process because it is
            // set up once and revisited, not worked in daily.
            getItem(t("Organisation"), "organisation", null, () => {
              navigate("/organisation");
            }),
          ]),
        ]
      : []),
    getItem(t("Languages"), "languages", <GlobalOutlined />, undefined, [
      getItem(t("English"), "en", null, () => {
        handleChangeLanguage("en");
      }),
      getItem(t("Kazakh"), "kk", null, () => {
        handleChangeLanguage("kk");
      }),
      getItem(t("Russian"), "ru", null, () => {
        handleChangeLanguage("ru");
      }),
      getItem(t("Chinese"), "zh", null, () => {
        handleChangeLanguage("zh");
      }),
    ]),
  ];

  const handleChangeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);
    window.location.reload();
  };

  function handleLogout(): void {
    // The next person to sign in may be on a differently configured
    // deployment; the cached settings answer must not follow them in.
    resetAppSettings();
    resetMyActionsCount();
    accountsApi.logout();
    navigate("/login");
  }

  const checkUserToken = () => {
    if (!userInfo || !userInfo.access) {
      setIsLoggedIn(false);
      navigate("/login");
      return;
    }
    setIsLoggedIn(true);
  };

  const loadTemplateTypes = async () => {
    const templateTypes = await ApiService.apiMainTemplateTypesList("admin");
    const userMenuItems: MenuItem[] = templateTypes.map((tt, index) =>
      getItem(
        t(tt.name),
        `${tt.id}_${index}`,
        <SnippetsOutlined />,
        undefined,
        tt.filtered_templates.map((p: Template, index) =>
          getItem(t(p.name), `${p.short_name}_${index}}`, null, () => {
            navigate(`/requests/${tt.short_name}/${p.short_name}`);
          }),
        ),
      ),
    );
    setTemplateItems(userMenuItems);
  };

  const items: MenuItem[] = [
    ...systemMenuItems,
    ...templateItems,
    ...administrationMenuItems,
  ];

  useEffect(() => {
    checkUserToken(); // eslint-disable-next-line react-hooks/exhaustive-deps
    loadTemplateTypes();
  }, [isLoggedIn]);

  // Keeping the badge honest. Three triggers, each covering what the others
  // cannot: signing in (first read), coming back to the window (someone else
  // assigned me something while I was elsewhere), and a slow poll for the same
  // reason while the window stays open. Work *this* user does is pushed
  // directly by the pages that do it, so those are the only gaps left.
  useEffect(() => {
    if (!isLoggedIn) return;
    refreshMyActionsCount();

    const onFocus = () => refreshMyActionsCount();
    window.addEventListener("focus", onFocus);
    const timer = window.setInterval(() => {
      // A backgrounded tab polling forever is pure noise; `focus` covers the
      // moment it matters again.
      if (!document.hidden) refreshMyActionsCount();
    }, 120000);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(timer);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
    }
  }, [i18n]);

  const text = userInfo ? userInfo.name : "User";
  const content = (
    <Menu selectable={false}>
      <MenuItem
        key="profile"
        onClick={() => {
          setPopoverOpen(false);
          setProfileOpen(true);
        }}
      >
        {t("My Profile")}
      </MenuItem>
      {/* Cover while away (D2). In the person menu rather than the sidebar
          because it is about *this person*, like their profile -- and because a
          permanent navigation entry for something most people arrange twice a
          year is a permanent entry most people learn to skip. */}
      <MenuItem
        key="delegations"
        onClick={() => {
          setPopoverOpen(false);
          navigate("/delegations");
        }}
      >
        {t("Cover while I am away")}
      </MenuItem>
      <MenuItem
        key="logout"
        onClick={() => {
          setPopoverOpen(false);
          handleLogout();
        }}
      >
        {t("Logout")}
      </MenuItem>
    </Menu>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Sider theme is scoped here so its dark-navy tokens don't leak
          into the rest of the app (which uses the global antdTheme). */}
      <ConfigProvider
        theme={{
          components: {
            Menu: {
              darkItemBg: SIDER_BG,
              darkSubMenuItemBg: SIDER_BG,
              darkItemSelectedBg: SIDER_SELECTED,
              // Brighter white menu text + clear hover highlight
              darkItemColor: "#ffffff",
              darkItemHoverColor: "#ffffff",
              darkItemHoverBg: "rgba(255,255,255,0.12)",
            },
            Layout: {
              siderBg: SIDER_BG,
              triggerBg: SIDER_BG,
            },
          },
        }}
      >
        <Sider
          className="app-sider"
          breakpoint="lg"
          collapsible
          theme="dark"
          width={SIDER_WIDTH}
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          // antd's own trigger is `position: fixed` against the *viewport* with
          // a width it copies from the rail. Two things then have to agree
          // about one number across every breakpoint, a collapse animation and
          // a horizontally scrolled page — and on a narrow screen they did not:
          // the button sat wider than the rail it belongs to. Rendered below as
          // an ordinary child instead, it is the rail's width by construction.
          trigger={null}
          // Sticky, because a permit is five thousand pixels long and the rail
          // used to scroll away with it: switching to another process meant
          // scrolling the whole case back to the top first. The *menu* scrolls,
          // not the rail, so the collapse control stays pinned to the bottom.
          style={{
            backgroundColor: SIDER_BG,
            position: "sticky",
            top: 0,
            height: "100vh",
          }}
        >
          {/* The brand block was 131px of the rail: a 105px logo, then the
              product name at 20px/700 — bigger than any page title in the
              product. Em-based margins put it off the 4px grid too. It is a
              signature, not a headline. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: space.snug,
              padding: `${space.base}px ${space.snug}px ${space.wide}px`,
            }}
          >
            <Image
              src="/logo.png"
              width={collapsed ? 28 : 34}
              preview={false}
            />
            {!collapsed && (
              <span
                style={{
                  color: "#ffffff",
                  fontSize: 15,
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                  lineHeight: 1.2,
                }}
              >
                {/* Product name — intentionally not translated. */}
                Safe Work System
              </span>
            )}
          </div>
          <Menu
            mode="inline"
            theme="dark"
            items={items}
            // The menu is the part that scrolls when a deployment has thirty
            // templates; the brand line and the collapse control stay put.
            style={{ backgroundColor: SIDER_BG, flex: 1, overflowY: "auto" }}
          />
          <button
            type="button"
            className="app-sider-trigger"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? t("Expand menu") : t("Collapse menu")}
            title={collapsed ? t("Expand menu") : t("Collapse menu")}
          >
            {collapsed ? <RightOutlined /> : <LeftOutlined />}
          </button>
        </Sider>
      </ConfigProvider>

      <Layout>
        {/* 48px, and it carries identity rather than a greeting.
            "Welcome, <name>" was 64px of the viewport spent on a sentence
            nobody reads twice — and the name was already one click away in
            this menu, which is where somebody goes to act on being that
            person (profile, sign out). The name is kept *beside* the avatar
            because a bare avatar makes people click to find out who they are
            signed in as. */}
        <Header
          style={{
            height: 48,
            lineHeight: "48px",
            padding: `0 ${space.wide}px`,
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            borderBottom: "1px solid #EBEDF0",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Popover
            placement="bottomRight"
            content={content}
            trigger="click"
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: space.tight,
                cursor: "pointer",
                padding: `0 ${space.tight}px`,
                height: 32,
                borderRadius: 6,
              }}
            >
              <Avatar shape="square" size={26} icon={<UserOutlined />} />
              <span style={{ fontSize: 13, color: "#33475B" }}>{text}</span>
            </span>
          </Popover>
        </Header>
        <Content style={{ backgroundColor: APP_BG }}>
          <div
            style={{
              // One value off the scale. It was 16/16/24 — three numbers for
              // one gutter, and the bottom one only existed to hold the footer
              // off the content.
              margin: space.base,
            }}
          >
            {isLoggedIn ? <TabbedWorkspace /> : null}
          </div>
        </Content>
        {/* Kept — it is a client attribution — but at the weight of a
            footnote. antd's default is `24px 50px`, which spent 66px of every
            screen on one line of 14px text. */}
        <Footer
          style={{
            textAlign: "center",
            backgroundColor: APP_BG,
            padding: `0 ${space.wide}px ${space.snug}px`,
            fontSize: 12,
            color: "#8C9196",
          }}
        >
          {t("Intellectual Property of Smart Solutions")}
        </Footer>
      </Layout>
      <MyProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
      />
    </Layout>
  );
};

export default MainLayout;
